import { mat4 } from 'gl-matrix';
import { useEffect, useState } from 'react';
import {
    DoseOutsideGrid,
    doseOutsideGrid,
} from '~/domain/dose/doseOutsideGrid';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { rigidVolumeMapping } from '~/domain/services/resampleVolume';
import { Grid } from '~/engine/data/common/Grid';
import { logErrorName } from '~/utils/logErrorName';

export type DoseOutsideGridState =
    | { readonly status: 'measuring' }
    | { readonly status: 'measured'; readonly outside: DoseOutsideGrid }
    | { readonly status: 'failed' };

// How much of a dose lies outside a grid in the same frame; undefined until both are given.
export function useDoseOutsideGrid(
    dose: DoseMatrix | undefined,
    grid: Grid | undefined
): DoseOutsideGridState | undefined {
    const [state, setState] = useState<{
        readonly dose: DoseMatrix;
        readonly grid: Grid;
        readonly result: DoseOutsideGridState;
    }>();

    useEffect(() => {
        if (!dose || !grid) {
            return;
        }
        let cancelled = false;
        doseOutsideGrid(
            dose.realPixels,
            dose.grid,
            grid,
            rigidVolumeMapping(mat4.create()),
            { isCancelled: () => cancelled }
        ).then(
            (outside) => {
                if (!cancelled && outside) {
                    setState({
                        dose,
                        grid,
                        result: { status: 'measured', outside },
                    });
                }
            },
            (error: unknown) => {
                logErrorName('Measuring dose outside a grid failed:', error);
                if (!cancelled) {
                    setState({ dose, grid, result: { status: 'failed' } });
                }
            }
        );
        return () => {
            cancelled = true;
        };
    }, [dose, grid]);

    if (!dose || !grid) {
        return undefined;
    }
    // A result for an earlier pick is never shown for the current one.
    return state?.dose === dose && state.grid === grid
        ? state.result
        : { status: 'measuring' };
}
